import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-search/dist/leaflet-search.min.css";
import "leaflet-search";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
// Default icons - adjust path to be relative or absolute based on where this file is
import pin from "../../assets/og/pin.png"; 

const formatPriceWithCommas = (price) => {
  return price ? price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "N/A";
};

const getSizeStyleMode = (styleConfig) => {
  return styleConfig.sizeField === "Unit_Type" ? "Unit_Type" : "plot_size";
};

const getInitialStyleMode = (styleConfig, canViewAvailability) => {
  return canViewAvailability ? styleConfig.mode : getSizeStyleMode(styleConfig);
};

export const buildTooltipContent = (plotsInfo, canViewAvailability = true) => {
  const formattedPrice = `Ksh. ${formatPriceWithCommas(plotsInfo.cash_price)}`;
  const statusRow = canViewAvailability
    ? `<b>Status:</b> ${plotsInfo.plot_status}<br />`
    : "";

  return `
   <div class="custom-tooltip-content">
      <b>Plots Information</b><br />
      <b>Plot No:</b> ${plotsInfo.plot_no}<br />
      ${statusRow}
      <b>Price:</b> ${formattedPrice}<br />
      <b>Size:</b> ${plotsInfo.plot_size || plotsInfo.Unit_Type} HA<br />
   </div>
   `;
};

const SharedMapComponent = ({
  geojsonData,
  plotUnitsData,
  center,
  zoom = 15,
  minZoom = 6,
  bounds,
  imageOverlays = [],
  styleConfig = { mode: "plot_status", sizeField: "plot_size" },
  customTypeColorFunction,
  customLegendItems = [],
  searchPrefix = "og", // Default prefix for search
  canViewAvailability = true,
}) => {
  const mapRef = useRef(null);
  const searchControlRef = useRef(null);
  const selectedPlotLayerRef = useRef(null);
  const geoJsonLayerRef = useRef(null); // Keep track of the layer
  const [currentStyleMode, setCurrentStyleMode] = useState(() =>
    getInitialStyleMode(styleConfig, canViewAvailability)
  );
  const effectiveStyleMode = canViewAvailability
    ? currentStyleMode
    : getSizeStyleMode(styleConfig);
  
  // Fullscreen & UI state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showLegend, setShowLegend] = useState(true);
  const containerRef = useRef(null);

  const goldenPinIcon = L.icon({
    iconUrl: pin,
    iconSize: [100, 80],
    iconAnchor: [16, 48],
  });

  useEffect(() => {
    if (!canViewAvailability) {
      setCurrentStyleMode(getSizeStyleMode(styleConfig));
    }
  }, [canViewAvailability, styleConfig]);

  // --- Map & Data Initialization ---
  useEffect(() => {
    if (!geojsonData || !geojsonData.features || geojsonData.features.length === 0) return;

    // 1. Initialize Map if needed
    if (!mapRef.current) {
      let initialCenter = center;
      if (!initialCenter) {
        // Auto-calculate center
        const firstFeature = geojsonData.features[0];
        const coordinates = firstFeature.geometry.coordinates[0];
        if (coordinates && coordinates.length >= 3) {
          const centerLng =
            coordinates.reduce((sum, coord) => sum + coord[0], 0) /
            coordinates.length;
          const centerLat =
            coordinates.reduce((sum, coord) => sum + coord[1], 0) /
            coordinates.length;
          initialCenter = [centerLat, centerLng];
        }
      }

      if (initialCenter) {
        mapRef.current = L.map("map", {
          minZoom: minZoom,
          zoomControl: false,
          preferCanvas: true, 
        }).setView(initialCenter, zoom);

        L.control.zoom({ position: "topleft" }).addTo(mapRef.current);

        if (bounds) {
             mapRef.current.setMaxBounds(bounds);
        }

        L.tileLayer(
          "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAzklEQVRIDb3BAQEAAAABIP5mz6iDBTAYzFRMXAuN7ABTGYMKEzzLMvBDFRgwoTPsDAUxiwwTM82zIAxUYMKRM+wMBTGLDNEzPMDEUYMKRM+wMBTGJ6m8F9B5K3RgEoYMKRM+wMBTGYsM0TMsxMVGAwTKhM+wMBTGJ6SBDHAk4MwBaxjAzRM9bAtwRWWoMKEz7AwFMb7JzEz7AwFMb7JzEz7AwFMb7JzEz7AwFMb7JzEz7AwFMb7JzEz7AwFMb7JzEz7AwFMb7JzEz7AwF9AAAAABJRU5ErkJggg=="
        ).addTo(mapRef.current);

        // Add Image Overlays
        imageOverlays.forEach(({ imageUrl, imageBounds }) => {
          const overlay = L.imageOverlay(imageUrl, imageBounds).addTo(mapRef.current);
          overlay.bringToFront();
        });
      }
    }

    // 2. Add/Update GeoJSON Layer
    // We only remove if it exists to support hot reloading or data changes, strictly speaking typical use case is static geojson per page.
    if (mapRef.current && !geoJsonLayerRef.current) {
        geoJsonLayerRef.current = L.geoJSON(geojsonData, {
            style: styleFunction,
            onEachFeature: onEachFeature,
        }).addTo(mapRef.current);
    }
    
    // 3. Initialize Search (Dependent on GeoJSON Layer)
    if (mapRef.current && geoJsonLayerRef.current && !searchControlRef.current) {
        searchControlRef.current = new L.Control.Search({
            layer: geoJsonLayerRef.current,
            propertyName: "Unit_Number",
            marker: false,
            autoCollapse: true,
            position: "topright",
            initial: false,
            style: "background-color: #f0feff; border: 2px solid #0078d4; border-radius: 4px; padding: 5px;",
            textPlaceholder: "Enter Plot Number...",
            textErr: "Plot not found!",
            textCancel: "Cancel",
            searchMove: false,
        });

        // Custom Move
        searchControlRef.current._moveToLocation = function (latlng, title, map) {
            map.setView(latlng, map.getZoom(), { animate: true, duration: 0.5 });
        };

        // Custom Search Text
        searchControlRef.current._searchText = (text) => {
            // Apply prefix if it's a number
            if (!isNaN(text) && searchPrefix) {
                text = searchPrefix + text;
            }
            L.Control.Search.prototype._searchText.call(searchControlRef.current, text);
        };

        // Found Event
        searchControlRef.current.on("search:locationfound", (e) => {
            if (selectedPlotLayerRef.current) {
                mapRef.current.removeLayer(selectedPlotLayerRef.current);
            }
            const selectedPlot = e.layer;
            selectedPlotLayerRef.current = L.marker(
                selectedPlot.getBounds().getCenter(),
                { icon: goldenPinIcon }
            ).addTo(mapRef.current);

            mapRef.current.fitBounds(e.layer.getBounds());

            setTimeout(() => {
                if (selectedPlotLayerRef.current) {
                    mapRef.current.removeLayer(selectedPlotLayerRef.current);
                }
            }, 3000);
        });

        searchControlRef.current.on("search:searchcomplete", (e) => {
             // Optional: Handle results list sorting if needed
        });

        searchControlRef.current.addTo(mapRef.current);
    }
  }, [geojsonData]); // Dependency on data

  // --- Dynamic Styling Updates ---
  useEffect(() => {
    if (geoJsonLayerRef.current) {
        geoJsonLayerRef.current.eachLayer((layer) => {
            if (layer.feature) {
                const newStyle = styleFunction(layer.feature);
                layer.setStyle(newStyle);
                
                // Also update tooltip content if needed - tricky with Leaflet strict binding
                // Usually simply re-binding or relying on re-render isn't enough for Leaflet
                // But style changes (color) are key here.
            }
        });
    }
  }, [effectiveStyleMode, plotUnitsData, styleConfig, canViewAvailability]);


  // --- Helpers ---
  const styleFunction = (feature) => {
    const plot_no = feature.properties.Unit_Number;
    let fillColor = "#f0fdff";
    let color = "black";
    let weight = 2;

    if (plotUnitsData) {
      const plotsInfo = plotUnitsData.find((p) => p.plot_no === plot_no);
      if (plotsInfo) {
        if (canViewAvailability && effectiveStyleMode === "plot_status") {
            const status = plotsInfo.plot_status;
            if (status === "Open") fillColor = "#49e183";
            else if (status === "Reserved") fillColor = "#ffd700";
            else if (status === "Sold") fillColor = "#db1515";
        } else if (effectiveStyleMode === "plot_size" || effectiveStyleMode === "Unit_Type") {
             if (customTypeColorFunction) {
                 const customResult = customTypeColorFunction(plotsInfo);
                 if (customResult) {
                     if (typeof customResult === 'object') {
                         fillColor = customResult.fillColor || getDefaultSizeColor(plotsInfo);
                         color = customResult.color || color;
                         weight = customResult.weight || weight;
                     } else {
                         fillColor = customResult;
                     }
                 } else {
                     fillColor = getDefaultSizeColor(plotsInfo);
                 }
             } else {
                 fillColor = getDefaultSizeColor(plotsInfo);
             }
        }
      }
    }
    return {
      fillColor: fillColor,
      fillOpacity: 0.9,
      color: color,
      weight: weight,
    };
  };

  const getDefaultSizeColor = (plotsInfo) => {
      const landSizeRaw = plotsInfo[styleConfig.sizeField] || plotsInfo.plot_size;
      if (!landSizeRaw) return "#f0fdff";
      
      const landSizeHectares = parseFloat(landSizeRaw.toString().replace(" HA", ""));
      const landSizeAcres = landSizeHectares * 2.47105;

      if (landSizeAcres <= 0.19) return "#2098ae"; 
      if (landSizeAcres >= 0.2 && landSizeAcres <= 0.3) return "#9966cc"; 
      if (landSizeAcres >= 0.4 && landSizeAcres <= 0.69) return "#ffe135"; 
      if (landSizeAcres >= 0.8 && landSizeAcres <= 1.3) return "#660000"; 
      if (landSizeAcres >= 1.8 && landSizeAcres <= 2.5) return "#489754"; 
      if (landSizeAcres >= 4.5) return "#ff0202"; 
      
      return "#f0fdff";
  };

  const onEachFeature = (feature, layer) => {
    const plot_no = feature.properties.Unit_Number;
    // We can't rely on 'plotUnitsData' being fresh in this closure on init.
    // However, tooltips generated on the fly might work?
    // Leaflet's 'bindTooltip' binds static content usually. 
    // To support dynamic content (data loaded after map init), we should intercept the open event or use a function for content.
    
    // For now, binding checking variable. If data isn't loaded, map might init without data. 
    // But parent ensures loading state. So data should be present.
    
    if (plotUnitsData) { 
        const plotsInfo = plotUnitsData.find((p) => p.plot_no === plot_no);
        if (plotsInfo) {
             const plotIdLabel = L.divIcon({
                className: "plot-id-label",
                html: `<strong><span style="font-size: 8px; font-weight: bold;">${plotsInfo.plot_no}</span></strong>`,
                iconSize: [20, 10],
            });
            L.marker(layer.getBounds().getCenter(), { icon: plotIdLabel }).addTo(mapRef.current);

            const tooltipContent = buildTooltipContent(plotsInfo, canViewAvailability);
             
             layer.bindTooltip(tooltipContent, { className: "custom-tooltip", direction: "top" });
             
             // Events
             if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
                 layer.on("click", function() { this.openTooltip(); });
             } else {
                 layer.on("mouseover", function() { this.openTooltip(); });
                 layer.on("mouseout", function() { this.closeTooltip(); });
             }
        }
    }
  };


  // --- Actions ---
  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
        containerRef.current.requestFullscreen().catch(console.error);
    } else {
        document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFs = !!document.fullscreenElement;
      setIsFullscreen(isFs);
      // Wait slightly for DOM update then invalidate size
      setTimeout(() => {
        if (mapRef.current) {
           mapRef.current.invalidateSize(); 
        }
      }, 100);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const handleDownloadPdf = async () => {
    if (!containerRef.current) return;
    try {
        const canvas = await html2canvas(containerRef.current, {
            useCORS: true,
            allowTaint: true,
            scale: 2, // Retain quality
        });
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({ orientation: "landscape" });
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        pdf.save("map-view.pdf");
    } catch (err) {
        console.error("PDF generation failed:", err);
    }
  };

  return (
    <div ref={containerRef} className={`relative w-full ${isFullscreen ? "h-screen flex items-center justify-center bg-white" : ""}`}>
      {/* Map Container */}
      <div id="map" style={{ height: isFullscreen ? "100%" : "500px", width: "100%" }} />

      {/* Controls - Pushed down to avoid overlapping with Leaflet Search (topright) */}
      <div className="absolute top-24 right-2 flex flex-col gap-2 z-[1000]">
        <button onClick={toggleFullscreen} className="btn btn-sm btn-circle bg-white text-black shadow-md border-0 hover:bg-gray-100" title="Toggle Fullscreen">
           {isFullscreen ? <MinimizeIcon /> : <MaximizeIcon />}
        </button>
        <button onClick={handleDownloadPdf} className="btn btn-sm btn-circle bg-white text-black shadow-md border-0 hover:bg-gray-100" title="Download PDF">
            <DownloadIcon />
        </button>
        {isFullscreen && (
            <button onClick={() => setShowLegend(!showLegend)} className="btn btn-sm btn-circle bg-white text-black shadow-md border-0 hover:bg-gray-100" title="Toggle Legend">
                <LegendIcon />
            </button>
        )}
      </div>

       {/* Mode Toggle */}
      {canViewAvailability && (
        <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-[1000] bg-white p-2 rounded-md shadow-md text-center opacity-90 hover:opacity-100 transition-opacity">
          <span className="text-xs font-bold uppercase tracking-wider block mb-1">Status | Size</span>
          <input
              type="checkbox"
              className="toggle toggle-sm toggle-success"
              checked={effectiveStyleMode === "plot_size" || effectiveStyleMode === "Unit_Type"}
              onChange={(e) => setCurrentStyleMode(e.target.checked ? getSizeStyleMode(styleConfig) : "plot_status")}
          />
        </div>
      )}

      {/* Legend */}
      {showLegend && (
          <div className="absolute bottom-6 left-6 z-[1000] bg-white/95 p-3 rounded-lg shadow-xl border border-gray-100 text-sm">
             {canViewAvailability && effectiveStyleMode === "plot_status" ? (
                 <div className="flex flex-col gap-2">
                     <LegendItem color="#49e183" label="Open" />
                     <LegendItem color="#ffd700" label="Reserved" />
                     <LegendItem color="#db1515" label="Sold" />
                 </div>
             ) : (
                 <div className="flex flex-col gap-2">
                    <LegendItem color="#2098ae" label="Eighth" />
                    <LegendItem color="#9966cc" label="Quarter" />
                    <LegendItem color="#ffe135" label="Half" />
                    <LegendItem color="#660000" label="Acre" />
                    <LegendItem color="#489754" label="2 Acres" />
                    <LegendItem color="#ff0202" label="5 Acres" />
                    {customLegendItems.map((item, idx) => (
                        <LegendItem key={idx} color={item.color} label={item.label} />
                    ))}
                 </div>
             )}
          </div>
      )}
      
       <style>
        {`
          .leaflet-tooltip.custom-tooltip {
            background-color: #86bc39;
            color: black;
            border: 1px solid white;
            font-size: 12px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            font-family: sans-serif;
            font-weight: 500;
          }
          .plot-id-label {
             background: transparent;
             border: none;
             box-shadow: none;
          }
        `}
      </style>
    </div>
  );
};

// Simple Icons to avoid external dep complications if feather/lucide not perfectly setup
const MaximizeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>
);
const MinimizeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/></svg>
);
const DownloadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
);
const LegendIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg>
);

const LegendItem = ({ color, label }) => (
    <div className="flex items-center">
        <div className="w-4 h-4 rounded-full mr-2 shadow-sm border border-gray-200" style={{ backgroundColor: color }} />
        <span className="text-gray-700 font-medium">{label}</span>
    </div>
);

export default SharedMapComponent;
